import Edit from "@mui/icons-material/Edit";
import {
  Box,
  Card,
  CardContent,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { xsHeight, mdHeight } from "components/AppBar";
import BasicMap from "components/BasicMap";
import { tabsOffset } from "components/TabLinks";
import CopyableTypography from "components/Typography/CopyableTypography";
import { useCanEditFacility } from "hooks/useAuthorization";
import { useMemo, useState } from "react";

import { useFacility, useMailingAddressIssues, usePhysicalAddressIssues } from "../useFacility";
import { EditBasicsDialog } from "./EditBasicsDialog";
import { V2DisplayOnlyField as DisplayOnlyField } from "./V2DisplayOnlyField";

export const ADDRESS_SECTION_ID = "address";
export const ADDRESS_SECTION_ICON = "🗺️";

function formatOneLiner(
  street1?: string | null,
  street2?: string | null,
  city?: string | null,
  state?: string | null,
  zip?: string | null
) {
  const street = [street1, street2].filter(Boolean).join(" ");
  const cityState = [city, state].filter(Boolean).join(", ");
  return [street, cityState, zip].filter(Boolean).join(" ");
}

export const AddressSection: React.FC = () => {
  const theme = useTheme();
  const { data } = useFacility();
  const facility = data?.facility;
  const [editOpen, setEditOpen] = useState(false);
  const canEdit = useCanEditFacility();

  const physicalAddressIssues = usePhysicalAddressIssues();
  const mailingAddressIssues = useMailingAddressIssues();

  const hasCoordinates =
    facility?.latitude != null &&
    facility?.longitude != null &&
    !Number.isNaN(facility.latitude) &&
    !Number.isNaN(facility.longitude);

  const physicalAddress = useMemo(
    () =>
      formatOneLiner(
        facility?.streetAddress1,
        facility?.streetAddress2,
        facility?.city,
        facility?.state,
        facility?.zip
      ),
    [
      facility?.streetAddress1,
      facility?.streetAddress2,
      facility?.city,
      facility?.state,
      facility?.zip,
    ]
  );

  const mailingAddress = useMemo(
    () =>
      formatOneLiner(
        facility?.mailingStreetAddress1,
        facility?.mailingStreetAddress2,
        facility?.mailingCity,
        facility?.mailingState,
        facility?.mailingZip
      ),
    [
      facility?.mailingStreetAddress1,
      facility?.mailingStreetAddress2,
      facility?.mailingCity,
      facility?.mailingState,
      facility?.mailingZip,
    ]
  );

  return (
    <Box
      id={ADDRESS_SECTION_ID}
      sx={{
        scrollMarginTop: {
          xs: theme.spacing(xsHeight + tabsOffset + 11),
          md: theme.spacing(mdHeight + tabsOffset + 11),
        },
        "& .edit-icon": { opacity: 0, transition: "opacity 200ms" },
        "&:hover .edit-icon": { opacity: 1 },
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={5}>
        <Typography variant="h6">{ADDRESS_SECTION_ICON} Address</Typography>
        {canEdit && (
          <Tooltip title="Edit Section">
            <IconButton
              size="small"
              className="edit-icon"
              sx={{ color: "action.active", mr: 2 }}
              onClick={() => setEditOpen(true)}
            >
              <Edit />
            </IconButton>
          </Tooltip>
        )}
      </Stack>

      {editOpen && <EditBasicsDialog onClose={() => setEditOpen(false)} />}

      <Stack direction="row" spacing={5} flexWrap="wrap" useFlexGap>
        {/* Map */}
        <Box sx={{ width: "240px", height: "196px", flexShrink: 0 }}>
          {hasCoordinates ? (
            <BasicMap latitude={facility?.latitude} longitude={facility?.longitude} static />
          ) : (
            <Card variant="outlined" sx={{ height: "100%" }}>
              <CardContent
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Map will display once latitude and longitude are available.
                </Typography>
              </CardContent>
            </Card>
          )}
        </Box>

        {/* Addresses */}
        <Stack spacing={4} sx={{ minWidth: 0 }}>
          <DisplayOnlyField
            label={hasCoordinates ? "Physical Address (Shown on map)" : "Physical Address"}
            text={physicalAddress}
            issues={physicalAddressIssues}
            renderValue={() => (
              <CopyableTypography
                text={physicalAddress}
                sx={{ whiteSpace: "normal", wordBreak: "break-word" }}
                tooltipOptions={{ disableHoverMessage: true }}
              />
            )}
          />
          <DisplayOnlyField
            label="Mailing Address"
            text={mailingAddress}
            issues={mailingAddressIssues}
            renderValue={() => (
              <CopyableTypography
                text={mailingAddress}
                sx={{ whiteSpace: "normal", wordBreak: "break-word" }}
                tooltipOptions={{ disableHoverMessage: true }}
              />
            )}
          />
        </Stack>
      </Stack>
    </Box>
  );
};
